import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import CurrencySelectionModal from "../../modal/CurrencySelectionModal";

interface UploadOfferLetterProps {
  candidateData: {
    name: string;
    position: string;
  };
  onUpload: (file: File) => void;
  onCancel: () => void;
  onSendToCandidate: (file: File, startDate: string, expiresAt: string, salary: string, currencyType: string) => void;
  isUploading: boolean;
}

export default function UploadOfferLetter({
  candidateData,
  onUpload,
  onCancel,
  onSendToCandidate,
  isUploading,
}: UploadOfferLetterProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [salary, setSalary] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState<string>("$");
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [errors, setErrors] = useState<{ file?: string; startDate?: string; expiresAt?: string; salary?: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("job-details");

  const { jobId } = useParams<{ jobId: string }>();
  const jobListings = useJobListSelector();
  const currentJob = jobListings?.find((job) => job.emp_job_id == jobId);

  const { currencies } = useSelector((state: any) => ({
    currencies: state.global.currencies,
  }));
  const currenciesData = currencies && currencies.length > 0 ? currencies : null;

  // Set currency from currentJob
  useEffect(() => {
    if (currentJob?.currency_name) {
      setSelectedCurrency(currentJob.currency_name);

      // Find and set the currency symbol from currenciesData
      if (currenciesData) {
        const currency = currenciesData.find((c: any) => c.currency === currentJob.currency_name);
        if (currency?.currency_symbol) {
          setSelectedCurrencySymbol(currency.currency_symbol);
        }
      }
    }
  }, [currentJob, currenciesData]);

  const handleFileSelect = (file: File) => {
    if (file && file.type === "application/pdf") {
      setUploadedFile(file);
      setErrors((prev) => ({ ...prev, file: undefined }));
      // Simulate upload progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress >= 100) {
          clearInterval(progressInterval);
        }
      }, 100);
    } else {
      alert(t("jobDetails.finalDecision.upload.pdfOnly"));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validate = () => {
    const newErrors: { file?: string; startDate?: string; expiresAt?: string; salary?: string } = {};
    if (!uploadedFile || uploadProgress < 100) {
      newErrors.file = "Please upload a PDF file";
    }
    if (!startDate) {
      newErrors.startDate = "Joining date is required";
    }
    if (!expiresAt || parseInt(expiresAt) < 1) {
      newErrors.expiresAt = "Offer end is required";
    }
    if (!salary) {
      newErrors.salary = "Salary is required";
    } else if (salary.length < 3) {
      newErrors.salary = "Salary must be at least 3 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendToCandidate = () => {
    if (!validate()) return;
    if (uploadedFile) {
      onSendToCandidate(uploadedFile, startDate, expiresAt, salary, selectedCurrency);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="upload-offer-letter">
      <div className="upload-offer-letter__header">
        <h3 className="upload-offer-letter__title">{t("jobDetails.finalDecision.upload.title")}</h3>
      </div>

      <p className="upload-offer-letter__description">{t("jobDetails.finalDecision.upload.description")}</p>

      {!uploadedFile ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`upload-offer-letter__drop-zone ${isDragOver ? "upload-offer-letter__drop-zone--drag-over" : ""}`}
          onClick={handleUploadClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileInputChange}
            className="upload-offer-letter__file-input"
          />

          <div className="upload-offer-letter__upload-icon">
            <i className="fa fa-upload"></i>
          </div>

          <h4 className="upload-offer-letter__upload-title">
            <span>{t("jobDetails.finalDecision.upload.clickToUpload")}</span>{" "}
            {t("jobDetails.finalDecision.upload.orDragDrop")}
          </h4>
          <p className="upload-offer-letter__file-size-info">{t("jobDetails.finalDecision.upload.maxFileSize")}</p>
          <p className="upload-offer-letter__file-type-info">{t("jobDetails.finalDecision.upload.pdfOnly")}</p>
        </div>
      ) : (
        <div className="upload-offer-letter__file-preview">
          <div className="upload-offer-letter__file-info">
            <div className="upload-offer-letter__file-details">
              <div className="upload-offer-letter__file-icon">
                <i className="fa fa-file-pdf-o"></i>
              </div>
              <div>
                <p className="upload-offer-letter__file-name">{uploadedFile.name}</p>
                <p className="upload-offer-letter__file-size">{formatFileSize(uploadedFile.size)}</p>
              </div>
            </div>
            <button onClick={handleRemoveFile} className="upload-offer-letter__remove-btn">
              <i className="fa fa-trash"></i>
            </button>
          </div>

          {uploadProgress < 100 && (
            <div className="upload-offer-letter__progress">
              <div className="upload-offer-letter__progress-info">
                <span className="upload-offer-letter__progress-text">
                  {t("jobDetails.finalDecision.upload.uploading")}
                </span>
                <span className="upload-offer-letter__progress-text">{uploadProgress}%</span>
              </div>
              <div className="upload-offer-letter__progress-bar">
                <div className="upload-offer-letter__progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
      {errors.file && <span className="ai-offer-generator__error-text">{errors.file}</span>}
      {/* {uploadedFile && uploadProgress >= 100 && ( */}
      <div className="upload-offer-letter__date-fields">
        <div className="upload-offer-letter__date-field">
          <label htmlFor="startDate" className="upload-offer-letter__date-label">
            Joining Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setErrors((prev) => ({ ...prev, startDate: undefined })); }}
            onClick={(e) => {
              try {
                const input = e.currentTarget as any;
                if (input.showPicker) {
                  input.showPicker();
                }
              } catch (error) {
                // Fallback for browsers that don't support showPicker
                console.log("showPicker not supported");
              }
            }}
            className={`upload-offer-letter__date-input ${errors.startDate ? "ai-offer-generator__input--error" : ""}`}
          />
          {errors.startDate && <span className="ai-offer-generator__error-text">{errors.startDate}</span>}
        </div>
        <div className="upload-offer-letter__date-field">
          <label htmlFor="expiresAt" className="upload-offer-letter__date-label">
            Offer End
          </label>
          <input
            type="number"
            inputMode="numeric"
            min="1"
            id="expiresAt"
            value={expiresAt}
            placeholder="Enter number of days"
            onChange={(e) => { setExpiresAt(e.target.value); setErrors((prev) => ({ ...prev, expiresAt: undefined })); }}
            className={`upload-offer-letter__date-input ${errors.expiresAt ? "ai-offer-generator__input--error" : ""}`}
          />
          {errors.expiresAt && <span className="ai-offer-generator__error-text">{errors.expiresAt}</span>}
        </div>
      </div>
      {/* )} */}

      <div className="upload-offer-letter__date-fields">
        <div className="upload-offer-letter__date-field" style={{ flex: "0 0 calc(50% - 8px)" }}>
          <label htmlFor="salary" className="upload-offer-letter__date-label">
            Salary
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={9}
              id="salary"
              value={salary}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 9);
                setSalary(value);
                setErrors((prev) => ({ ...prev, salary: undefined }));
              }}
              placeholder="Enter salary"
              className={`upload-offer-letter__date-input ${errors.salary ? "ai-offer-generator__input--error" : ""}`}
              style={{ paddingLeft: "50px" }}
            />
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {selectedCurrency}
            </span>
          </div>
          {errors.salary && <span className="ai-offer-generator__error-text">{errors.salary}</span>}
          <div className="currency-change">
            <button type="button" className="currency-btn" onClick={() => setShowCurrencyModal(true)}>
              {selectedCurrency} <span>{t("jobDetails.finalDecision.aiGenerator.form.changeCurrency")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="upload-offer-letter__actions">
        <button type="button" onClick={onCancel} className="btn-cancel">
          {t("jobDetails.finalDecision.upload.cancel")}
        </button>

        <button
          type="button"
          onClick={handleSendToCandidate}
          disabled={isUploading}
          className="btn-preview"
        >
          {isUploading
            ? t("jobDetails.finalDecision.upload.sending")
            : t("jobDetails.finalDecision.upload.sendToCandidate")}
        </button>
      </div>

      {showCurrencyModal && (
        <CurrencySelectionModal
          isOpen={showCurrencyModal}
          onClose={() => setShowCurrencyModal(false)}
          currenciesData={currenciesData}
          selectedCurrency={selectedCurrency}
          onCurrencySelect={(currency: string, currencySymbol: string) => {
            setSelectedCurrency(currency);
            setSelectedCurrencySymbol(currencySymbol);
          }}
        />
      )}
    </div>
  );
}
